import React, { useRef } from 'react';
import { Form as FinalForm, Field } from 'react-final-form';
import classNames from 'classnames';

import { useIntl } from '../../../../util/reactIntl';
import { isMainSearchTypeKeywords } from '../../../../util/search';

import Form from '../../../../components/Form/Form';
import LocationAutocompleteInput from '../../../../components/LocationAutocompleteInput/LocationAutocompleteInput';

import IconSearchDesktop from './IconSearchDesktop';
import css from './TopbarSearchForm.module.css';

const identity = v => v;

const KeywordSearchField = props => {
  const { keywordSearchWrapperClasses, iconClass, intl, isMobile = false, inputRef, onSearchIconClick, showInput = true, onCloseClick } = props;
  return (
    <div className={keywordSearchWrapperClasses}>
      <button
        className={css.searchSubmit}
        aria-label={intl.formatMessage({ id: 'TopbarDesktop.screenreader.search' })}
        onClick={onSearchIconClick}
        type="button"
      >
        <div className={iconClass}>
          <IconSearchDesktop />
        </div>
      </button>

      {showInput && (
        <Field
          name="keywords"
          render={({ input, meta }) => {
            return (
              <input
                className={isMobile ? css.mobileInput : css.desktopInput}
                {...input}
                id={isMobile ? 'keyword-search-mobile' : 'keyword-search'}
                data-testid={isMobile ? 'keyword-search-mobile' : 'keyword-search'}
                ref={inputRef}
                type="text"
                placeholder={intl.formatMessage({
                  id: 'TopbarSearchForm.placeholder',
                })}
                autoComplete="off"
              />
            );
          }}
        />
      )}

      {showInput && (
        <button
          className={css.closeButton}
          onClick={onCloseClick}
          type="button"
          aria-label={intl.formatMessage({ id: 'TopbarSearchForm.close' })}
        >
          ×
        </button>
      )}
    </div>
  );
};

const LocationSearchField = props => {
  const { desktopInputRootClass, intl, isMobile = false, inputRef, onLocationChange, onSearchIconClick, showInput = true, onCloseClick } = props;
  const submitButton = ({ }) => (
    <button
      className={css.searchSubmit}
      aria-label={intl.formatMessage({ id: 'TopbarDesktop.screenreader.search' })}
      onClick={onSearchIconClick}
      type="button"
    >
      <IconSearchDesktop />
    </button>
  );
  return (
    <>
      {showInput && (
        <Field
          name="location"
          format={identity}
          render={({ input, meta }) => {
            const { onChange, ...restInput } = input;

            // Merge the standard onChange function with custom behaviur. A better solution would
            // be to use the FormSpy component from Final Form and pass onChange to the
            // onChange prop but that breaks due to insufficient subscription handling.
            // See: https://github.com/final-form/react-final-form/issues/159
            const searchOnChange = value => {
              onChange(value);
              onLocationChange(value);
            };

            return (
              <LocationAutocompleteInput
                className={isMobile ? css.mobileInputRoot : desktopInputRootClass}
                iconClassName={isMobile ? css.mobileIcon : css.desktopIcon}
                inputClassName={isMobile ? css.mobileInput : css.desktopInput}
                predictionsClassName={isMobile ? css.mobilePredictions : css.desktopPredictions}
                predictionsAttributionClassName={isMobile ? css.mobilePredictionsAttribution : null}
                placeholder={intl.formatMessage({ id: 'TopbarSearchForm.placeholder' })}
                closeOnBlur={!isMobile}
                inputRef={inputRef}
                input={{ ...restInput, onChange: searchOnChange }}
                meta={meta}
                submitButton={submitButton}
                ariaLabel={intl.formatMessage({ id: 'TopbarDesktop.screenreader.search' })}
              />
            );
          }}
        />
      )}
      {showInput && (
        <button
          className={css.closeButton}
          onClick={onCloseClick}
          type="button"
          aria-label={intl.formatMessage({ id: 'TopbarSearchForm.close' })}
        >
          ×
        </button>
      )}
    </>
  );
};

/**
 * The main search form for the Topbar.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.desktopInputRoot root class for desktop form input
 * @param {Function} props.onSubmit
 * @param {boolean} props.isMobile
 * @param {Object} props.appConfig
 * @param {Function?} props.onSearchIconClick callback for search icon click
 * @param {Function?} props.onCloseClick callback for close button click
 * @param {boolean} props.showInput whether to show the input field (for modal usage)
 * @returns {JSX.Element} search form element
 */
const TopbarSearchForm = props => {
  const searchInpuRef = useRef(null);
  const intl = useIntl();

  const { appConfig, onSubmit, onSearchIconClick, onCloseClick, showInput = true, ...restOfProps } = props;

  const onChange = location => {
    if (!isMainSearchTypeKeywords(appConfig) && location.selectedPlace) {
      // Note that we use `onSubmit` instead of the conventional
      // `handleSubmit` prop for submitting. We want to autosubmit
      // when a place is selected, and don't require any extra
      // validations for the form.
      onSubmit({ location });
      // blur search input to hide software keyboard
      searchInpuRef?.current?.blur();
    }
  };

  const onKeywordSubmit = values => {
    if (isMainSearchTypeKeywords(appConfig)) {
      onSubmit({ keywords: values.keywords });
      // blur search input to hide software keyboard
      searchInpuRef?.current?.blur();
    }
  };

  const onLocationSubmit = values => {
    // Allow submit button click for an empty location search form
    if (!isMainSearchTypeKeywords(appConfig)) {
      onSubmit({ location: values.location });
    }
  };

  const isKeywordsSearch = isMainSearchTypeKeywords(appConfig);
  const submit = isKeywordsSearch ? onKeywordSubmit : onLocationSubmit;
  return (
    <FinalForm
      {...restOfProps}
      onSubmit={submit}
      render={formRenderProps => {
        const {
          rootClassName,
          className,
          desktopInputRoot,
          isMobile = false,
          handleSubmit,
        } = formRenderProps;
        const classes = classNames(rootClassName, className);
        const desktopInputRootClass = desktopInputRoot || css.desktopInputRoot;

        const keywordSearchWrapperClasses = classNames(
          css.keywordSearchWrapper,
          isMobile ? css.mobileInputRoot : desktopInputRootClass
        );

        return (
          <Form className={classes} onSubmit={handleSubmit} enforcePagePreloadFor="SearchPage">
            {isKeywordsSearch ? (
              <KeywordSearchField
                keywordSearchWrapperClasses={keywordSearchWrapperClasses}
                iconClass={classNames(isMobile ? css.mobileIcon : css.desktopIcon || css.icon)}
                intl={intl}
                isMobile={isMobile}
                inputRef={searchInpuRef}
                onSearchIconClick={onSearchIconClick}
                showInput={showInput}
                onCloseClick={onCloseClick}
              />
            ) : (
              <LocationSearchField
                desktopInputRootClass={desktopInputRootClass}
                intl={intl}
                isMobile={isMobile}
                inputRef={searchInpuRef}
                onLocationChange={onChange}
                onSearchIconClick={onSearchIconClick}
                showInput={showInput}
                onCloseClick={onCloseClick}
              />
            )}
          </Form>
        );
      }}
    />
  );
};

export default TopbarSearchForm;
